import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Pill, Plus, Trash2, Clock, AlertCircle, CheckCircle2, RefreshCw, Shield, Info, UserCheck, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import healthApi from '@/api/healthApi';
import MedicationIntelligencePanel from '@/components/MedicationIntelligencePanel';
import MedicationRiskAlerts from '@/components/MedicationRiskAlerts';
import IntelligentSpecialistRecommendation from '@/components/IntelligentSpecialistRecommendation';
import NeedHelpButton from '@/components/NeedHelpButton';
import AppFooter from '@/components/AppFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicationSupport = () => {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [refillAlerts, setRefillAlerts] = useState([]);
  const [adherenceStats, setAdherenceStats] = useState(null);
  const [adherenceInsights, setAdherenceInsights] = useState(null);
  const [medicationIntelligence, setMedicationIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refillDialogOpen, setRefillDialogOpen] = useState(false);
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [supportRequestDialogOpen, setSupportRequestDialogOpen] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [refillQuantity, setRefillQuantity] = useState('');
  const [snoozeDays, setSnoozeDays] = useState(3);
  const [activeTab, setActiveTab] = useState('medications');
  
  const [supportRequestData, setSupportRequestData] = useState({
    urgency: 'medium',
    preferred_contact_method: 'email',
    description: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: 'daily',
    times: [],
    reminder_enabled: true,
    notes: '',
    refill_quantity: '',
    refill_alert_threshold: 7,
    reminder_frequency_type: 'both',
    reminder_days_threshold: 7,
    reminder_percentage_threshold: 25
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  // Fetch adherence insights when Tracking tab is selected
  useEffect(() => {
    if (activeTab === 'tracking') {
      fetchAdherenceInsights();
    } else if (activeTab === 'support') {
      fetchMedicationIntelligence();
    }
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchMedications(),
        fetchRefillAlerts(),
        fetchAdherenceStats()
      ]);
    } catch (error) {
      console.error('Error fetching medication data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedicationIntelligence = async () => {
    try {
      const { data } = await healthApi.getMedicationIntelligence();
      setMedicationIntelligence(data);
    } catch (error) {
      console.error('Error fetching medication intelligence:', error);
    }
  };

  const fetchMedications = async () => {
    try {
      const { data } = await axios.get(`${API}/medications`, { withCredentials: true });
      setMedications(data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  };

  const fetchRefillAlerts = async () => {
    try {
      const { data } = await axios.get(`${API}/medications/refill-alerts`, { withCredentials: true });
      setRefillAlerts(data.alerts || []);
    } catch (error) {
      console.error('Error fetching refill alerts:', error);
    }
  };

  const fetchAdherenceStats = async () => {
    try {
      const { data } = await axios.get(`${API}/medications/adherence-stats`, { withCredentials: true });
      setAdherenceStats(data);
    } catch (error) {
      console.error('Error fetching adherence stats:', error);
    }
  };

  const fetchAdherenceInsights = async () => {
    try {
      const { data } = await axios.get(`${API}/medications/adherence-insights`, { withCredentials: true });
      setAdherenceInsights(data);
    } catch (error) {
      console.error('Error fetching adherence insights:', error);
    }
  };

  const handleAddTime = () => {
    setFormData({ ...formData, times: [...formData.times, '09:00'] });
  };

  const handleTimeChange = (index, value) => {
    const newTimes = [...formData.times];
    newTimes[index] = value;
    setFormData({ ...formData, times: newTimes });
  };

  const handleRemoveTime = (index) => {
    setFormData({ ...formData, times: formData.times.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.dosage) {
      toast.error('Please fill in dosage (required field)');
      return;
    }

    try {
      const submitData = {
        ...formData,
        refill_quantity: formData.refill_quantity ? parseInt(formData.refill_quantity) : null,
        refill_remaining: formData.refill_quantity ? parseInt(formData.refill_quantity) : null,
        refill_alert_threshold: parseInt(formData.refill_alert_threshold)
      };

      await axios.post(`${API}/medications`, submitData, { withCredentials: true });
      toast.success('Medication added successfully!');
      setDialogOpen(false);
      setFormData({ 
        name: '', 
        dosage: '', 
        frequency: 'daily', 
        times: [], 
        reminder_enabled: true, 
        notes: '',
        refill_quantity: '',
        refill_alert_threshold: 7,
        reminder_frequency_type: 'both',
        reminder_days_threshold: 7,
        reminder_percentage_threshold: 25
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this medication?')) {
      try {
        await axios.delete(`${API}/medications/${id}`, { withCredentials: true });
        toast.success('Medication deleted');
        fetchAllData();
      } catch (error) {
        toast.error('Failed to delete medication');
      }
    }
  };

  const handleMarkTaken = async (medId) => {
    try {
      await axios.post(`${API}/medications/${medId}/mark-taken`, {}, { withCredentials: true });
      toast.success('Marked as taken ✓');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSkipDose = async (medId) => {
    try {
      await axios.post(`${API}/medications/${medId}/skip-dose`, {}, { withCredentials: true });
      toast.info('Dose marked as skipped');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleRefillSubmit = async () => {
    if (!refillQuantity || !selectedMed) return;
    
    try {
      await axios.post(
        `${API}/medications/${selectedMed.id}/refill?refill_quantity=${parseInt(refillQuantity)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Refill recorded!');
      setRefillDialogOpen(false);
      setSelectedMed(null);
      setRefillQuantity('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to record refill');
    }
  };

  const handleSnoozeReminder = async () => {
    if (!selectedMed) return;
    
    try {
      await axios.post(
        `${API}/medications/${selectedMed.id}/snooze-reminder?snooze_days=${snoozeDays}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Reminder snoozed for ${snoozeDays} days`);
      setSnoozeDialogOpen(false);
      setSelectedMed(null);
      setSnoozeDays(3);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    }
  };

  const handleDismissReminder = async (medId) => {
    try {
      await axios.post(
        `${API}/medications/${medId}/dismiss-reminder`,
        {},
        { withCredentials: true }
      );
      toast.info('Reminder dismissed');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const handleSubmitSupportRequest = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/medication-support-requests`,
        supportRequestData,
        { withCredentials: true }
      );
      toast.success('Support request submitted successfully! A specialist will contact you soon.');
      setSupportRequestDialogOpen(false);
      setSupportRequestData({
        urgency: 'medium',
        preferred_contact_method: 'email',
        description: ''
      });
    } catch (error) {
      toast.error('Failed to submit support request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-healthloop-mint-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthloop-mint-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-healthloop-mint-50 via-teal-50 to-cyan-50 p-4">
      <div className="max-w-6xl mx-auto pt-4 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Pill className="h-8 w-8 mr-3 text-healthloop-mint-600" />
            Medication Support
          </h1>
          <p className="text-gray-600 mt-2">Manage your medications safely and privately</p>
        </div>



        {/* Important Disclaimer - Prominent Placement */}
        <Card className="mb-6 border-2 border-healthloop-mint-200 bg-gradient-to-r from-healthloop-mint-50 to-teal-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-blue-700" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2 text-base">Important: What HealthLoop Provides</h3>
                <div className="space-y-2 text-sm text-blue-900">
                  <p>
                    <strong>HealthLoop does not provide prescriptions or direct medication delivery.</strong> This platform supports medication tracking, reminders, and access to professional support resources.
                  </p>
                  <p className="text-blue-800">
                    We're here to help you stay organized and connected with the care you need. For prescriptions, refills, or medical advice, please contact your healthcare provider.
                  </p>
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-900 font-medium">
                      ⚠️ <strong>For urgent or medical emergencies:</strong> Contact your healthcare provider immediately or call emergency services (911). This platform is not for emergency medical situations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Notice */}
        <Card className="mb-6 border-2 border-healthloop-mint-200 bg-healthloop-mint-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-healthloop-mint-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-healthloop-mint-900">
                  <strong>Your Privacy Matters:</strong> All medication information is stored securely and privately. This tool helps you track medications but does not replace professional medical advice.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refill Alerts Banner */}
        {refillAlerts.length > 0 && (
          <Card className="mb-6 border-2 border-orange-200 bg-orange-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-orange-900">
                <AlertCircle className="h-5 w-5 mr-2" />
                Refill Reminders ({refillAlerts.length})
              </CardTitle>
              <CardDescription className="text-orange-800">
                Friendly reminders to help you stay prepared
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {refillAlerts.map((alert, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-orange-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 mb-1">{alert.medication.name}</p>
                        <p className="text-sm text-gray-700 mb-2">{alert.message}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span>💊 {alert.doses_remaining} doses left</span>
                          <span>📊 {alert.percentage_remaining}% remaining</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedMed(alert.medication);
                            setRefillDialogOpen(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Mark Refilled
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedMed(alert.medication);
                            setSnoozeDialogOpen(true);
                          }}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Snooze
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDismissReminder(alert.medication.id)}
                          className="text-gray-600"
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="medications" className="flex items-center gap-2">
              <Pill className="h-4 w-4" />
              My Medications
            </TabsTrigger>
            <TabsTrigger value="tracking" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Tracking
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Support
            </TabsTrigger>
          </TabsList>

          {/* Medications Tab */}
          <TabsContent value="medications" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Medication
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Medication</DialogTitle>
                    <DialogDescription>Enter your medication details securely</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Medication Name (optional for privacy)</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Morning Pill or leave blank"
                      />
                      <p className="text-xs text-gray-500">You can use a generic name for privacy</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dosage">Dosage *</Label>
                      <Input
                        id="dosage"
                        value={formData.dosage}
                        onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                        placeholder="e.g., 500mg"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="frequency">Frequency</Label>
                      <Select value={formData.frequency} onValueChange={(value) => setFormData({ ...formData, frequency: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="twice_daily">Twice Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="as_needed">As Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Reminder Times</Label>
                      {formData.times.map((time, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            type="time"
                            value={time}
                            onChange={(e) => handleTimeChange(index, e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveTime(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" onClick={handleAddTime} className="w-full">
                        <Clock className="h-4 w-4 mr-2" />
                        Add Time
                      </Button>

                    <div className="space-y-3 p-4 bg-healthloop-mint-50 rounded-lg border border-healthloop-mint-200">
                      <Label className="text-base font-semibold text-blue-900">Refill Reminder Settings</Label>
                      <p className="text-xs text-blue-700 mb-3">Customize when you'd like to be reminded about refills</p>
                      
                      <div className="space-y-2">
                        <Label htmlFor="reminder_type">Reminder Type</Label>
                        <Select 
                          value={formData.reminder_frequency_type} 
                          onValueChange={(value) => setFormData({ ...formData, reminder_frequency_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both">Both (Days & Percentage)</SelectItem>
                            <SelectItem value="days">Days Before Running Out</SelectItem>
                            <SelectItem value="percentage">Percentage Remaining</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(formData.reminder_frequency_type === 'days' || formData.reminder_frequency_type === 'both') && (
                        <div className="space-y-2">
                          <Label htmlFor="reminder_days">Remind me when I have this many days left</Label>
                          <Input
                            id="reminder_days"
                            type="number"
                            value={formData.reminder_days_threshold}
                            onChange={(e) => setFormData({ ...formData, reminder_days_threshold: e.target.value })}
                            placeholder="7"
                            min="1"
                            max="30"
                          />
                          <p className="text-xs text-gray-500">Default: 7 days</p>
                        </div>
                      )}

                      {(formData.reminder_frequency_type === 'percentage' || formData.reminder_frequency_type === 'both') && (
                        <div className="space-y-2">
                          <Label htmlFor="reminder_percentage">Remind me when I have this percentage left</Label>
                          <Input
                            id="reminder_percentage"
                            type="number"
                            value={formData.reminder_percentage_threshold}
                            onChange={(e) => setFormData({ ...formData, reminder_percentage_threshold: e.target.value })}
                            placeholder="25"
                            min="1"
                            max="100"
                          />
                          <p className="text-xs text-gray-500">Default: 25%</p>
                        </div>
                      )}
                    </div>

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="refill_quantity">Refill Quantity (optional)</Label>
                      <Input
                        id="refill_quantity"
                        type="number"
                        value={formData.refill_quantity}
                        onChange={(e) => setFormData({ ...formData, refill_quantity: e.target.value })}
                        placeholder="e.g., 30 pills"
                      />
                      <p className="text-xs text-gray-500">Track when you need a refill</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="refill_alert_threshold">Alert When Remaining</Label>
                      <Input
                        id="refill_alert_threshold"
                        type="number"
                        value={formData.refill_alert_threshold}
                        onChange={(e) => setFormData({ ...formData, refill_alert_threshold: e.target.value })}
                        placeholder="e.g., 7"
                      />
                      <p className="text-xs text-gray-500">Get reminded when this many doses remain</p>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="reminder">Enable Reminders</Label>
                      <Switch
                        id="reminder"
                        checked={formData.reminder_enabled}
                        onCheckedChange={(checked) => setFormData({ ...formData, reminder_enabled: checked })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Any special instructions?"
                        rows={3}
                      />
                    </div>

                    <Button type="submit" className="w-full">
                      Add Medication
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {medications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Pill className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No medications added yet</p>
                  <Button onClick={() => setDialogOpen(true)}>Add Your First Medication</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {medications.map((med) => (
                  <Card key={med.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <Pill className="h-5 w-5 text-healthloop-mint-600 mr-2" />
                            <h3 className="text-xl font-semibold">{med.name}</h3>
                          </div>
                          <p className="text-gray-600 mb-2">Dosage: {med.dosage}</p>
                          <p className="text-sm text-gray-500 mb-2">Frequency: {med.frequency.replace('_', ' ')}</p>
                          
                          {med.times && med.times.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {med.times.map((time, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {time}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Refill Info */}
                          {med.refill_remaining !== null && (
                            <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">
                                <strong>Refill Status:</strong> {med.refill_remaining} doses remaining
                                {med.refill_remaining <= med.refill_alert_threshold && (
                                  <span className="ml-2 text-orange-600 font-semibold">⚠️ Low</span>
                                )}
                              </p>
                            </div>
                          )}

                          {med.notes && (
                            <p className="text-sm text-gray-600 mt-2">
                              <span className="font-semibold">Notes:</span> {med.notes}
                            </p>
                          )}

                          <div className="flex gap-2 mt-3">
                            {med.reminder_enabled && (
                              <Badge variant="secondary">
                                🔔 Reminders ON
                              </Badge>
                            )}
                            {med.last_taken && (
                              <Badge variant="secondary">
                                Last taken: {new Date(med.last_taken).toLocaleString()}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkTaken(med.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Taken
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSkipDose(med.id)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            Skip
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(med.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tracking Tab */}
          <TabsContent value="tracking" className="space-y-6">
            {adherenceInsights && (
              <>
                {/* Overall Adherence Message */}
                <Card className="bg-gradient-to-r from-healthloop-mint-50 to-teal-50 border-2 border-healthloop-mint-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <Info className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-base text-blue-900 font-medium">
                          {adherenceInsights.overall_message}
                        </p>
                        {adherenceInsights.adherence_rate > 0 && (
                          <p className="text-sm text-blue-700 mt-2">
                            Current adherence rate: <strong>{adherenceInsights.adherence_rate}%</strong>
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Adherence Insights */}
                {adherenceInsights.insights && adherenceInsights.insights.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Your Adherence Insights</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {adherenceInsights.insights.map((insight, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border-2 ${
                            insight.type === 'positive' ? 'bg-green-50 border-green-200' :
                            insight.type === 'achievement' ? 'bg-healthloop-mint-50 border-healthloop-mint-200' :
                            insight.type === 'alert' ? 'bg-orange-50 border-orange-200' :
                            'bg-healthloop-mint-50 border-healthloop-mint-200'
                          }`}
                        >
                          <p className="text-sm flex items-start">
                            <span className="text-lg mr-2">{insight.icon}</span>
                            <span className="flex-1">{insight.message}</span>
                          </p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {adherenceStats && (
              <>
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardHeader>
                    <CardTitle>Medication Adherence Statistics</CardTitle>
                    <CardDescription>Your medication tracking overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Total Medications</p>
                        <p className="text-3xl font-bold text-indigo-700">{adherenceStats.total_medications}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Doses Taken</p>
                        <p className="text-3xl font-bold text-green-700">{adherenceStats.total_doses_taken}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Doses Skipped</p>
                        <p className="text-3xl font-bold text-orange-700">{adherenceStats.total_doses_missed}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Adherence Rate</p>
                        <p className="text-3xl font-bold text-blue-700">{adherenceStats.adherence_rate}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-healthloop-mint-50 border-2 border-healthloop-mint-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Privacy Note:</strong> All medication data stays on your device and in your secure account. You have full control over what information you track.
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-4">
            {/* Positioning Statement */}
            <Card className="border-2 border-healthloop-mint-200 bg-gradient-to-r from-healthloop-mint-50 to-teal-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-healthloop-mint-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-healthloop-mint-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-healthloop-mint-900 mb-1">
                      HealthLoop bridges the gap between self-care and clinical care
                    </p>
                    <p className="text-xs text-indigo-700">
                      Intelligent medication tracking that helps you stay safe, consistent, and connected to professional support when needed.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Medication Intelligence */}
            {medicationIntelligence?.personalized_insights && medicationIntelligence.personalized_insights.length > 0 && (
              <MedicationIntelligencePanel insights={medicationIntelligence.personalized_insights} />
            )}

            {/* Medication Risk Alerts */}
            {medicationIntelligence && (
              <MedicationRiskAlerts risks={medicationIntelligence.risk_alerts || []} />
            )}

            {/* Intelligent Specialist Recommendation */}
            {medicationIntelligence?.specialist_recommendation && (
              <IntelligentSpecialistRecommendation 
                recommendation={medicationIntelligence.specialist_recommendation}
                onRequestSupport={() => setSupportRequestDialogOpen(true)}
              />
            )}

            {/* Fallback when no intelligence data */}
            {!medicationIntelligence && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <UserCheck className="h-6 w-6 mr-2 text-indigo-600" />
                    Medication Support Resources
                  </CardTitle>
                  <CardDescription>Access confidential support and guidance</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-healthloop-mint-50 border border-healthloop-mint-200 rounded-lg">
                    <h4 className="font-semibold text-indigo-900 mb-2">Loading Intelligence...</h4>
                    <p className="text-sm text-indigo-800">
                      Analyzing your medication patterns to provide personalized insights.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Medication Support Request */}
            <div className="p-4 bg-healthloop-mint-50 border-2 border-healthloop-mint-200 rounded-lg">
              <h4 className="font-semibold text-healthloop-mint-900 mb-2 flex items-center">
                <UserCheck className="h-5 w-5 mr-2" />
                Request Medication Support
              </h4>
              <p className="text-sm text-healthloop-mint-800 mb-3">
                Need help accessing your medication? Our support team can connect you with resources and assistance.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSupportRequestDialogOpen(true)}
                className="border-healthloop-mint-300 text-healthloop-mint-700 hover:bg-healthloop-mint-100"
                data-testid="request-support-button"
              >
                Request Support
              </Button>
              <p className="text-xs text-healthloop-mint-700 mt-2" data-testid="confidential-notice">
                🔒 <strong>Confidential:</strong> Your request will be handled privately and securely
              </p>
            </div>

            {/* Expert Support (Optional) */}
            <div className="p-5 bg-gradient-to-r from-healthloop-mint-50 to-teal-50 border-2 border-healthloop-mint-200 rounded-lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-12 h-12 bg-healthloop-mint-100 rounded-full flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-healthloop-mint-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-healthloop-mint-900 text-base">Expert Support (Optional)</h4>
                  </div>
                  <p className="text-sm text-healthloop-mint-800 mb-3 leading-relaxed">
                    If you need extra guidance, you can connect with qualified professionals to support your health journey.
                  </p>
                  <Link to="/talk-to-specialist">
                    <Button className="bg-healthloop-mint-500 hover:bg-healthloop-mint-600 text-white">
                      <UserCheck className="h-4 w-4 mr-2" />
                      Explore Support Options
                    </Button>
                  </Link>
                  <p className="text-xs text-healthloop-mint-700 mt-3">
                    💜 Support is here when you're ready—completely optional
                  </p>
                </div>
              </div>
            </div>

            {/* Next Phase: Pharmacy Integration */}
            <div className="p-4 bg-gradient-to-r from-healthloop-mint-50 to-teal-50 border-2 border-healthloop-mint-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 bg-healthloop-mint-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">🚀</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-healthloop-mint-900">Next Phase: Pharmacy Integration</h4>
                    <Badge className="bg-healthloop-mint-500 text-white text-xs">Product Roadmap</Badge>
                  </div>
                  <p className="text-sm text-healthloop-mint-800 mb-3 leading-relaxed">
                    We are building partnerships with licensed pharmacies to enable safe, confidential medication access and delivery. This will create seamless connections to trusted providers while maintaining your privacy and security.
                  </p>
                  <div className="flex items-start gap-2 bg-white/60 p-3 rounded-lg border border-healthloop-mint-200">
                    <Info className="h-4 w-4 text-healthloop-mint-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-healthloop-mint-900">
                      <strong>Why we're building this:</strong> To complete the care journey—from self-tracking to guided support and medication access—all in one place.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Medical Disclaimer */}
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-purple-900 font-semibold mb-1">Important Note</p>
                    <p className="text-sm text-purple-800 mb-3">
                      HealthLoop helps you track and manage your wellness journey, but does not replace professional medical advice or provide prescriptions.
                    </p>
                    <p className="text-sm text-purple-800">
                      <strong>Always consult your healthcare provider</strong> for medical decisions. For emergencies, contact your provider or call 911.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Refill Dialog */}
        <Dialog open={refillDialogOpen} onOpenChange={setRefillDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Medication Refill</DialogTitle>
              <DialogDescription>
                Update the refill quantity for {selectedMed?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="refill-quantity">New Refill Quantity</Label>
                <Input
                  id="refill-quantity"
                  type="number"
                  value={refillQuantity}
                  onChange={(e) => setRefillQuantity(e.target.value)}
                  placeholder="e.g., 30"
                />
                <p className="text-xs text-gray-500">How many doses did you refill?</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleRefillSubmit} className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Record Refill
                </Button>
                <Button variant="outline" onClick={() => setRefillDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Snooze Reminder Dialog */}
        <Dialog open={snoozeDialogOpen} onOpenChange={setSnoozeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Snooze Refill Reminder</DialogTitle>
              <DialogDescription>
                How long would you like to snooze this reminder for {selectedMed?.name}?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="snooze-days">Snooze for (days)</Label>
                <Select value={snoozeDays.toString()} onValueChange={(value) => setSnoozeDays(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">1 week</SelectItem>
                    <SelectItem value="14">2 weeks</SelectItem>
                    <SelectItem value="30">1 month</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">The reminder will reappear after this period</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSnoozeReminder} className="flex-1">
                  <Clock className="h-4 w-4 mr-2" />
                  Snooze Reminder
                </Button>
                <Button variant="outline" onClick={() => setSnoozeDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Request Medication Support Dialog */}
        <Dialog open={supportRequestDialogOpen} onOpenChange={setSupportRequestDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Medication Support</DialogTitle>
              <DialogDescription>
                Need help accessing medication? We're here to help connect you with resources.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmitSupportRequest} className="space-y-4">
              {/* Privacy Notice */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900" data-testid="privacy-notice">
                  🔒 <strong>Your request will be handled confidentially.</strong> This connects you with support resources—not direct medication delivery.
                </p>
              </div>

              {/* Urgency */}
              <div className="space-y-2">
                <Label htmlFor="urgency">How urgent is your need?</Label>
                <Select 
                  value={supportRequestData.urgency} 
                  onValueChange={(value) => setSupportRequestData({ ...supportRequestData, urgency: value })}
                  data-testid="urgency-select"
                >
                  <SelectTrigger data-testid="urgency-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low" data-testid="urgency-low">Low - Within a week</SelectItem>
                    <SelectItem value="medium" data-testid="urgency-medium">Medium - Within a few days</SelectItem>
                    <SelectItem value="high" data-testid="urgency-high">High - As soon as possible</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Contact Method */}
              <div className="space-y-2">
                <Label htmlFor="contact-method">Preferred Contact Method</Label>
                <Select 
                  value={supportRequestData.preferred_contact_method} 
                  onValueChange={(value) => setSupportRequestData({ ...supportRequestData, preferred_contact_method: value })}
                  data-testid="contact-method-select"
                >
                  <SelectTrigger data-testid="contact-method-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email" data-testid="contact-email">Email</SelectItem>
                    <SelectItem value="phone" data-testid="contact-phone">Phone</SelectItem>
                    <SelectItem value="both" data-testid="contact-both">Both (Email and Phone)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Description (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="description">Describe your need (optional)</Label>
                <Textarea
                  id="description"
                  data-testid="description-textarea"
                  value={supportRequestData.description}
                  onChange={(e) => setSupportRequestData({ ...supportRequestData, description: e.target.value })}
                  placeholder="Optional: Provide details about what you need help with..."
                  rows={3}
                />
                <p className="text-xs text-gray-500">Share as much or as little as you're comfortable with</p>
              </div>

              {/* Disclaimer */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-900" data-testid="disclaimer-notice">
                  <strong>Note:</strong> This is a support and referral service. A support specialist will contact you to help connect you with appropriate resources and assistance.


              {/* Future Feature Info */}
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-xs text-indigo-900">
                  <strong>Future Enhancement:</strong> HealthLoop plans to integrate with licensed pharmacies to enable safe and confidential medication access and delivery.
                </p>
              </div>

                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="submit-support-request">
                  Submit Request
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setSupportRequestDialogOpen(false)}
                  data-testid="cancel-support-request"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <NeedHelpButton />
      <AppFooter />
    </div>
  );
};

export default MedicationSupport;
